import React, { useState } from "react";
import {
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { commonStyles } from "../../../../common/commonStyles";
import RichTextEditor from "../../../../common/FormComponents/RichTextEditor";
import { CmpEventLibraryKindEnum } from "../../../../../graphql/types";
import {
  EventDressCode,
  EventRegistrationQuestion,
  EventRegistrationQuestionChoice,
  EventSpeaker,
  createDefaultRegistrationQuestions,
} from "../../eventMockData";
import { IdaComingSoonAffordance } from "../../../Members/memberWorkflowShared";
import {
  FieldLabel,
  WizardActionButton,
  WizardStepProps,
  wizardId,
  wizardTextFieldSx,
} from "./wizardShared";
import EventLibrarySelect, {
  DeleteConfirmModal,
  LibraryEditDialog,
} from "./EventLibrarySelect";
import EventTypeSelect from "./EventTypeSelect";

const dressCodes: EventDressCode[] = ["Formal", "Business Attire", "Casual"];

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

// Fallback presets for events created before the registrationQuestions
// form-state field existed. createBlankEventFormState seeds new events with
// the same presets, so this only renders for legacy/empty data.
const DEFAULT_REGISTRATION_QUESTIONS: EventRegistrationQuestion[] =
  createDefaultRegistrationQuestions();

// Section headings live OUTSIDE the bordered cards on this step (per the
// create-event screenshots), a structure the shared SectionShell cannot
// express — hence the local heading/card primitives. SectionCard's border
// chrome is aligned to SectionShell's tokens (border-black rounded-lg,
// Q-037 normalization); content and padding stay mock-bound.
const SectionHeading = ({
  title,
  required,
  lines = [],
  className = "",
}: {
  title: string;
  required?: boolean;
  lines?: string[];
  className?: string;
}): React.ReactElement => (
  <div className={className}>
    {/* Title/description sizes match Chamber Setup's SettingsCard heading
        pattern (July 9, 2026 direction). */}
    <h3 className="text-lg font-bold leading-6 text-black">
      {title}
      {required ? <span className="text-red-500"> *</span> : null}
    </h3>
    {lines.map((line) => (
      <p key={line} className="mt-1 max-w-4xl text-sm leading-6 text-black">
        {line}
      </p>
    ))}
  </div>
);

const SectionCard = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement => (
  <section
    className={`rounded-lg border border-black bg-white px-10 py-9 shadow-none ${className}`}
  >
    {children}
  </section>
);

// ---------------------------------------------------------------------------
// Image dropzone (EVT-CREATE-004): click-to-browse AND drag/drop read the file
// via FileReader.readAsDataURL and store the data URL on the event state.
// ---------------------------------------------------------------------------

const isRenderableImage = (value: string) =>
  /^(data:image|https?:|blob:|\/)/.test(value);

const ImageDropzone = ({
  value,
  onChange,
  compact = false,
}: {
  value: string;
  onChange: (dataUrl: string) => void;
  compact?: boolean;
}): React.ReactElement => {
  const [uploadError, setUploadError] = useState<string | null>(null);

  const readFile = (file: File | null | undefined) => {
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      setUploadError("Please upload a PNG or JPG image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("Image must be 5MB or smaller.");
      return;
    }
    setUploadError(null);
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  if (value && isRenderableImage(value)) {
    return (
      <div
        className={`relative overflow-hidden rounded-[14px] border border-gray-300 ${
          compact ? "min-h-[240px]" : ""
        }`}
      >
        <img
          src={value}
          alt="Uploaded"
          className={compact ? "h-[240px] w-full object-cover" : "max-h-[420px] w-full object-cover"}
        />
        <IconButton
          size="small"
          onClick={() => onChange("")}
          aria-label="Remove image"
          className="!absolute !right-3 !top-3"
          sx={{
            backgroundColor: "rgba(17,24,39,0.75)",
            color: "#fff",
            "&:hover": { backgroundColor: "#111827" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
    );
  }

  return (
    <div>
      <label
        className={`flex cursor-pointer flex-col items-center justify-center bg-white text-center ${
          compact
            ? "min-h-[240px] rounded-[10px] border border-dotted border-gray-500 px-4"
            : "min-h-[320px] rounded-[14px] border-2 border-dashed border-cpaGold px-6"
        }`}
        onDragOver={(dragEvent) => dragEvent.preventDefault()}
        onDrop={(dropEvent) => {
          dropEvent.preventDefault();
          readFile(dropEvent.dataTransfer.files?.[0]);
        }}
      >
        <input
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={({ target }) => {
            readFile(target.files?.[0]);
            target.value = "";
          }}
        />
        <CloudUploadIcon
          sx={{
            color: commonStyles.cpaGold,
            fontSize: compact ? 44 : 58,
            mb: compact ? 1.5 : 3,
          }}
        />
        <span
          className={`font-extrabold text-gray-950 ${compact ? "text-[15px]" : "text-[16px]"}`}
        >
          Drag and drop your image here
        </span>
        <span className={`mt-2 text-gray-950 ${compact ? "text-[14px]" : "text-[16px]"}`}>
          or click to <span className="text-cpaGoldDark underline">browse</span>
        </span>
        <span
          className={`mt-4 tracking-wide text-gray-700 ${compact ? "text-[11px]" : "text-[13px]"}`}
        >
          RECOMMENDED SIZE: 1280 X 720 PX
        </span>
        <span className={`mt-3 text-gray-700 ${compact ? "text-[11px]" : "text-[13px]"}`}>
          PNG or JPG, max 5MB
        </span>
      </label>
      {uploadError ? <p className="mt-2 text-sm text-red-600">{uploadError}</p> : null}
    </div>
  );
};

// ---------------------------------------------------------------------------

type SpeakerDraft = Omit<EventSpeaker, "id">;

const blankSpeakerDraft: SpeakerDraft = {
  name: "",
  company: "",
  title: "",
  imageUrl: "",
  bio: "",
};

const Step1EventDetails = ({
  event,
  setEvent,
  chamberId,
  errors,
}: WizardStepProps): React.ReactElement => {
  const update = <K extends keyof typeof event>(key: K, value: (typeof event)[K]) =>
    setEvent((prev) => ({ ...prev, [key]: value }));

  // ---- Speaker flow (EVT-CREATE-006/007): each speaker profile is saved from
  // an isolated draft, so adding another speaker never overrides prior ones.
  const speakers = event.speakers || [];
  const [speakerFormOpen, setSpeakerFormOpen] = useState(false);
  const [editingSpeakerId, setEditingSpeakerId] = useState<string | null>(null);
  const [speakerDraft, setSpeakerDraft] = useState<SpeakerDraft>(blankSpeakerDraft);
  const [speakerErrors, setSpeakerErrors] = useState<Record<string, string>>({});
  const [speakerToDelete, setSpeakerToDelete] = useState<EventSpeaker | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const patchSpeakerDraft = (patch: Partial<SpeakerDraft>) =>
    setSpeakerDraft((prev) => ({ ...prev, ...patch }));

  const openBlankSpeakerForm = () => {
    setEditingSpeakerId(null);
    setSpeakerDraft(blankSpeakerDraft);
    setSpeakerErrors({});
    setSpeakerFormOpen((prev) => (editingSpeakerId ? true : !prev));
  };

  const openEditSpeaker = (speaker: EventSpeaker) => {
    setEditingSpeakerId(speaker.id);
    setSpeakerDraft({
      name: speaker.name,
      company: speaker.company,
      title: speaker.title,
      imageUrl: speaker.imageUrl,
      bio: speaker.bio,
    });
    setSpeakerErrors({});
    setSpeakerFormOpen(true);
  };

  const saveSpeakerProfile = () => {
    const nextErrors: Record<string, string> = {};
    if (!speakerDraft.name.trim()) nextErrors.name = "Speaker name is required.";
    if (!speakerDraft.imageUrl) nextErrors.image = "Speaker image is required.";
    if (!stripHtml(speakerDraft.bio)) nextErrors.bio = "Speaker bio is required.";
    setSpeakerErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (editingSpeakerId) {
      update(
        "speakers",
        speakers.map((speaker) =>
          speaker.id === editingSpeakerId ? { ...speaker, ...speakerDraft } : speaker
        )
      );
    } else {
      update("speakers", [...speakers, { id: wizardId("speaker"), ...speakerDraft }]);
    }
    setEditingSpeakerId(null);
    setSpeakerDraft(blankSpeakerDraft);
    setSpeakerFormOpen(false);
  };

  const dropSpeakerRow = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    const next = [...speakers];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    update("speakers", next);
    setDragIndex(null);
  };

  // ---- Additional Registration Questions (EVT-CREATE-009), per the
  // contract.md registrationQuestions model.
  const questions: EventRegistrationQuestion[] = event.registrationQuestions?.length
    ? event.registrationQuestions
    : DEFAULT_REGISTRATION_QUESTIONS;
  const setQuestions = (next: EventRegistrationQuestion[]) =>
    update("registrationQuestions", next);
  const patchQuestion = (
    questionId: string,
    patch: Partial<EventRegistrationQuestion>
  ) =>
    setQuestions(
      questions.map((question) =>
        question.id === questionId ? { ...question, ...patch } : question
      )
    );
  const setChoiceEnabled = (questionId: string, choiceId: string, enabled: boolean) =>
    setQuestions(
      questions.map((question) =>
        question.id === questionId
          ? {
              ...question,
              choices: question.choices.map((choice: EventRegistrationQuestionChoice) =>
                choice.id === choiceId ? { ...choice, enabled } : choice
              ),
            }
          : question
      )
    );

  const [expandedQuestionId, setExpandedQuestionId] = useState<string | null>(null);
  const [questionDraft, setQuestionDraft] = useState("");
  const [editingQuestion, setEditingQuestion] =
    useState<EventRegistrationQuestion | null>(null);
  const [editingQuestionTitle, setEditingQuestionTitle] = useState("");
  const [editingQuestionDescription, setEditingQuestionDescription] = useState("");

  const openQuestionEdit = (question: EventRegistrationQuestion) => {
    if (question.kind === "speakerQuestion") {
      setExpandedQuestionId(question.id);
      setQuestionDraft(question.label || "");
      return;
    }
    setEditingQuestion(question);
    setEditingQuestionTitle(question.adminTitle);
    setEditingQuestionDescription(question.description);
  };

  return (
    <>
      {/* ------------------------------------------------------------- */}
      {/* Event Details                                                   */}
      {/* ------------------------------------------------------------- */}
      <div>
        <SectionHeading
          title="Event Details"
          className="mb-6"
          lines={[
            "Add the event details about your event. This information will appear on your website on the event calendar and on the event page Hero Banner.",
          ]}
        />
        <SectionCard>
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            <EventLibrarySelect
              chamberId={chamberId}
              kind={CmpEventLibraryKindEnum.Title}
              label="Event Title"
              required
              hint="Add a new event title or edit or select previous event title from dropdown"
              placeholder="Select Event Title"
              value={event.title}
              onChange={(value) => update("title", value)}
              error={errors.title}
            />
            <EventTypeSelect
              chamberId={chamberId}
              value={event.eventTypeId}
              currentLabel={event.eventType}
              currentColor={event.eventTypeColor}
              onChange={({ id, label, color }) =>
                setEvent((prev) => ({
                  ...prev,
                  eventTypeId: id,
                  eventType: label,
                  eventTypeColor: color,
                }))
              }
              error={errors.eventType}
            />
          </div>

          <div className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-2">
            <EventLibrarySelect
              chamberId={chamberId}
              kind={CmpEventLibraryKindEnum.Code}
              label="Event Code (Optional)"
              hint="Add an optional event code for internal reporting. Use the dropdown to view and select from previously used codes."
              placeholder="add unique event code"
              value={event.eventCode || ""}
              onChange={(value) => update("eventCode", value)}
            />
          </div>

          <div className="mt-10">
            {/* No flex-wrap: the IDA affordance must sit far right of the row
                even on narrow screens instead of wrapping to the left
                (July 9, 2026 direction). */}
            <div className="flex items-start justify-between gap-8">
              <div className="min-w-0 max-w-[900px] flex-1">
                <FieldLabel
                  required
                  hint="Provide brief details about the event that will appear on the event banner to help users understand what to expect and why they should attend. Let your Intelligent Digital Assistant IDA help generate your chamber’s event description."
                >
                  Brief Event Description for Hero Banner
                </FieldLabel>
              </div>
              {/* IDA affordance (EVT-CREATE-003) */}
              <IdaComingSoonAffordance />
            </div>
            <RichTextEditor
              value={event.heroShortDescription || ""}
              onChange={(html) => update("heroShortDescription", html)}
              placeholder="Describe the event, audience, what attendees will gain, keynote speakers, agenda highlights, networking opportunities, and any other important details."
              maxLength={250}
            />
          </div>

          <div className="mt-10">
            <FieldLabel hint="Upload an eye-catching image if you want it to appear in the background on the Event Hero Banner. (Make sure to use image only with no words)">
              Add Event Hero Banner Image to be used as Background Image
            </FieldLabel>
            <ImageDropzone
              value={event.featuredBanner}
              onChange={(dataUrl) => update("featuredBanner", dataUrl)}
            />
          </div>
        </SectionCard>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* Enter Event Details (registration template)                     */}
      {/* ------------------------------------------------------------- */}
      <SectionCard>
        {/* Same size/weight as the sibling field labels (EVT-CREATE-005). */}
        <FieldLabel
          required
          hint="Edit the section below that appears on the event Registration template."
        >
          Enter Event Details
        </FieldLabel>
        <RichTextEditor
          value={event.registrationDetailsHtml || ""}
          onChange={(html) => update("registrationDetailsHtml", html)}
          maxLength={50}
          minHeight={120}
        />
        <div className="mt-8">
          <FieldLabel required hint="Provide description of what type of event you are offering">
            Brief Event Description
          </FieldLabel>
          <RichTextEditor
            value={event.description}
            onChange={(html) => update("description", html)}
            maxLength={500}
          />
          {errors.description ? (
            <p className="mt-2 text-sm text-red-600">{errors.description}</p>
          ) : null}
        </div>
      </SectionCard>

      {/* ------------------------------------------------------------- */}
      {/* Add Keynote Speaker or Create Speaker Panel                     */}
      {/* ------------------------------------------------------------- */}
      <div>
        <SectionHeading
          title="Add Keynote Speaker or Create Speaker Panel"
          className="mb-6"
          lines={[
            "Add Speaker profile image and brief bio for your event. For panel create each speaker profile, to be displayed in your event page on your website.",
          ]}
        />
        <WizardActionButton tone="gold" startIcon={<AddIcon />} onClick={openBlankSpeakerForm}>
          Add Speaker Profile
        </WizardActionButton>

        {speakerFormOpen ? (
          <SectionCard className="mt-6">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <div>
                <FieldLabel required>Speaker Name</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter Speaker Name"
                  value={speakerDraft.name}
                  onChange={({ target }) => patchSpeakerDraft({ name: target.value })}
                  error={Boolean(speakerErrors.name)}
                  helperText={speakerErrors.name}
                  sx={wizardTextFieldSx}
                />
              </div>
              <div>
                <FieldLabel>Company Name</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter Company Name"
                  value={speakerDraft.company}
                  onChange={({ target }) => patchSpeakerDraft({ company: target.value })}
                  sx={wizardTextFieldSx}
                />
              </div>
              <div>
                <FieldLabel>Title</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter Speaker Title"
                  value={speakerDraft.title}
                  onChange={({ target }) => patchSpeakerDraft({ title: target.value })}
                  sx={wizardTextFieldSx}
                />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-[300px_minmax(0,1fr)]">
              <div>
                <FieldLabel required>Speaker Image</FieldLabel>
                <ImageDropzone
                  compact
                  value={speakerDraft.imageUrl}
                  onChange={(dataUrl) => patchSpeakerDraft({ imageUrl: dataUrl })}
                />
                {speakerErrors.image ? (
                  <p className="mt-2 text-sm text-red-600">{speakerErrors.image}</p>
                ) : null}
              </div>
              <div>
                <FieldLabel required>Speaker Bio</FieldLabel>
                <RichTextEditor
                  value={speakerDraft.bio}
                  onChange={(html) => patchSpeakerDraft({ bio: html })}
                  placeholder="Provide a brief bio for speaker which will display on event page."
                  maxLength={500}
                  minHeight={170}
                />
                {speakerErrors.bio ? (
                  <p className="mt-2 text-sm text-red-600">{speakerErrors.bio}</p>
                ) : null}
              </div>
            </div>

            <div className="mt-8">
              <WizardActionButton tone="gold" onClick={saveSpeakerProfile}>
                Save Speaker Profile
              </WizardActionButton>
            </div>
          </SectionCard>
        ) : null}
      </div>

      {/* ------------------------------------------------------------- */}
      {/* Speakers List                                                   */}
      {/* ------------------------------------------------------------- */}
      {speakers.length > 0 ? (
        <div>
          <SectionHeading
            title="Speakers List"
            className="mb-6"
            lines={[
              "Manage speaker profiles and arrange the order they appear on the event registration page.",
            ]}
          />
          <div className="overflow-x-auto rounded-[18px] border border-gray-300 bg-white">
            <div className="min-w-[980px]">
              <div className="grid grid-cols-[220px_1.2fr_1.2fr_1fr_1fr_180px] gap-3 bg-gray-50 px-6 py-4 text-[13px] font-extrabold uppercase tracking-wide text-gray-900">
                <span>Speaker Image</span>
                <span>Speaker Name</span>
                <span>Company Name</span>
                <span>Title</span>
                <span>Bio</span>
                <span>Actions</span>
              </div>
              {speakers.map((speaker, index) => (
                <div
                  key={speaker.id}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(dragEvent) => dragEvent.preventDefault()}
                  onDrop={() => dropSpeakerRow(index)}
                  onDragEnd={() => setDragIndex(null)}
                  className={`grid grid-cols-[220px_1.2fr_1.2fr_1fr_1fr_180px] items-center gap-3 border-t border-gray-200 px-6 py-4 ${
                    dragIndex === index ? "bg-gray-50" : "bg-white"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <DragIndicatorIcon
                      className="cursor-grab text-gray-500"
                      fontSize="small"
                    />
                    {isRenderableImage(speaker.imageUrl) ? (
                      <img
                        src={speaker.imageUrl}
                        alt={speaker.name}
                        className="h-14 w-14 rounded object-cover"
                      />
                    ) : (
                      <span className="h-14 w-14 rounded bg-gray-100" />
                    )}
                  </span>
                  <span className="truncate text-[17px] font-extrabold text-gray-950">
                    {speaker.name}
                  </span>
                  <span className="truncate text-[17px] text-gray-950">
                    {speaker.company}
                  </span>
                  <span className="truncate text-[15px] text-gray-950">
                    {speaker.title}
                  </span>
                  <span className="truncate text-[15px] text-gray-950">
                    {stripHtml(speaker.bio)}
                  </span>
                  <span className="flex gap-2">
                    <WizardActionButton
                      tone="gold"
                      size="small"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => openEditSpeaker(speaker)}
                    >
                      Edit
                    </WizardActionButton>
                    <WizardActionButton
                      tone="red"
                      size="small"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => setSpeakerToDelete(speaker)}
                    >
                      Delete
                    </WizardActionButton>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <DeleteConfirmModal
        open={Boolean(speakerToDelete)}
        heading="Delete Speaker Profile"
        name={speakerToDelete?.name || ""}
        onConfirm={() => {
          if (speakerToDelete) {
            update(
              "speakers",
              speakers.filter((speaker) => speaker.id !== speakerToDelete.id)
            );
            if (editingSpeakerId === speakerToDelete.id) {
              setEditingSpeakerId(null);
              setSpeakerDraft(blankSpeakerDraft);
              setSpeakerFormOpen(false);
            }
          }
          setSpeakerToDelete(null);
        }}
        onCancel={() => setSpeakerToDelete(null)}
      />

      {/* ------------------------------------------------------------- */}
      {/* Event Visibility (EVT-CREATE-008)                               */}
      {/* ------------------------------------------------------------- */}
      <div>
        <SectionHeading
          title="Event Visibility"
          required
          className="mb-5"
          lines={[
            "Control who can see this event. Public events will be shown on website for anyone to register. Private events will generate a link to access and register for event.",
          ]}
        />
        <RadioGroup
          row
          value={event.visibility}
          onChange={({ target }) =>
            update("visibility", target.value as typeof event.visibility)
          }
        >
          <FormControlLabel
            value="Public"
            control={<Radio />}
            sx={{ alignItems: "flex-start", mr: 12, "& .MuiRadio-root": { pt: 0.25 } }}
            label={
              <span className="block">
                <span className="block text-[17px] font-extrabold text-gray-950">
                  Public
                </span>
                <span className="block text-[15px] leading-6 text-gray-950">
                  Anyone can view and register
                </span>
              </span>
            }
          />
          <FormControlLabel
            value="Private"
            control={<Radio />}
            sx={{ alignItems: "flex-start", "& .MuiRadio-root": { pt: 0.25 } }}
            label={
              <span className="block">
                <span className="block text-[17px] font-extrabold text-gray-950">
                  Private
                </span>
                <span className="block text-[15px] leading-6 text-gray-950">
                  Only people with the link can view and register
                </span>
              </span>
            }
          />
        </RadioGroup>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* Event Dress Code                                                */}
      {/* ------------------------------------------------------------- */}
      <div>
        <SectionHeading
          title="Event Dress Code"
          className="mb-5"
          lines={[
            "To show a dress requirement for your event that will appear on your registration form, check off the appropriate option. If left blank it will not show on registration form",
          ]}
        />
        <RadioGroup
          row
          value={event.dressCode || ""}
          onChange={({ target }) => update("dressCode", target.value as EventDressCode)}
        >
          {dressCodes.map((code) => (
            <FormControlLabel
              key={code}
              value={code}
              control={<Radio />}
              sx={{ mr: 10 }}
              label={
                <span className="text-[17px] font-extrabold text-gray-950">{code}</span>
              }
            />
          ))}
        </RadioGroup>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* Additional Registration Questions (EVT-CREATE-009)              */}
      {/* ------------------------------------------------------------- */}
      <div>
        <SectionHeading
          title="Additional Registration Questions"
          className="mb-6"
          lines={[
            "Enable and edit preset question or create new custom questions.",
            "These questions will appear on the registration form for attendees to answer.",
          ]}
        />
        <div>
          <div className="grid grid-cols-[minmax(0,1fr)_240px_150px] items-center gap-4 bg-gray-50 px-6 py-4">
            <span className="text-[18px] font-extrabold text-gray-950">Questions</span>
            <span className="text-[18px] font-extrabold text-gray-950">
              Enable Questions
            </span>
            <span className="text-[18px] font-extrabold text-gray-950">Actions</span>
          </div>
          {questions.map((question) => {
            const expanded =
              question.kind === "speakerQuestion" && expandedQuestionId === question.id;
            return (
              <div key={question.id} className="border-t border-gray-200 px-6 py-6">
                <div className="grid grid-cols-[minmax(0,1fr)_240px_150px] items-start gap-4">
                  <div>
                    <p className="text-[18px] font-extrabold text-gray-950">
                      {question.adminTitle}
                    </p>
                    <p className="mt-1 max-w-[560px] text-[15px] leading-6 text-gray-600">
                      {question.description}
                    </p>
                    {question.kind === "multiSelect" ? (
                      <div className="mt-4 space-y-2">
                        {/* July 9, 2026 direction: the per-choice checkboxes
                            are gone — the ENABLE OPTION switch is the only
                            control on each choice row. */}
                        {question.choices.map((choice: EventRegistrationQuestionChoice) => (
                          <div key={choice.id} className="flex items-center">
                            <span className="w-[340px] text-[16px] text-gray-950">
                              {choice.label}
                            </span>
                            <Switch
                              checked={choice.enabled}
                              onChange={({ target }) =>
                                setChoiceEnabled(question.id, choice.id, target.checked)
                              }
                            />
                            <span className="ml-2 text-[12px] font-bold tracking-wide text-gray-500">
                              ENABLE OPTION
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-3 pt-1">
                    <Switch
                      checked={question.enabled}
                      onChange={({ target }) =>
                        patchQuestion(question.id, { enabled: target.checked })
                      }
                    />
                    <span className="text-[12px] font-bold tracking-wide text-gray-500">
                      {question.enabled ? "ENABLED" : "DISABLED"}
                    </span>
                  </div>
                  <div className="pt-1">
                    {expanded ? (
                      <WizardActionButton
                        tone="black"
                        size="small"
                        onClick={() => setExpandedQuestionId(null)}
                      >
                        Cancel
                      </WizardActionButton>
                    ) : (
                      <WizardActionButton
                        tone="gold"
                        size="small"
                        startIcon={<EditOutlinedIcon />}
                        onClick={() => openQuestionEdit(question)}
                      >
                        Edit
                      </WizardActionButton>
                    )}
                  </div>
                </div>

                {expanded ? (
                  <div className="mt-6 rounded-[18px] border border-gray-300 bg-white px-10 py-14">
                    <h4 className="text-[19px] font-extrabold text-gray-950">
                      Submit a Question
                    </h4>
                    <p className="mt-2 text-[15px] leading-6 text-gray-950">
                      {question.helperText ||
                        "Submit a question you would like asked of the speaker or panelists. We will do our best to ask it on your behalf, but cannot guarantee all questions will be included due to time availability."}
                    </p>
                    <input
                      className="mt-4 w-full rounded-[8px] border border-gray-300 px-4 py-3 text-[15px] text-gray-950 focus:border-cpaGold focus:outline-none"
                      placeholder="Enter question?"
                      maxLength={250}
                      value={questionDraft}
                      onChange={({ target }) => setQuestionDraft(target.value)}
                    />
                    <p className="mt-2 text-[13px] text-gray-500">
                      {questionDraft.length}/250 characters
                    </p>
                    <div className="mt-5">
                      <WizardActionButton
                        tone="gold"
                        onClick={() => {
                          patchQuestion(question.id, { label: questionDraft });
                          setExpandedQuestionId(null);
                        }}
                      >
                        Save Question View
                      </WizardActionButton>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit dialog for the multi-select preset questions (title/helper). */}
      <LibraryEditDialog
        open={Boolean(editingQuestion)}
        title="Edit Question"
        confirmLabel="Save Question"
        confirmDisabled={!editingQuestionTitle.trim()}
        onConfirm={() => {
          if (editingQuestion) {
            patchQuestion(editingQuestion.id, {
              adminTitle: editingQuestionTitle.trim(),
              description: editingQuestionDescription,
            });
          }
          setEditingQuestion(null);
        }}
        onClose={() => setEditingQuestion(null)}
      >
        <FieldLabel required>Question Title</FieldLabel>
        <TextField
          fullWidth
          size="small"
          value={editingQuestionTitle}
          onChange={({ target }) => setEditingQuestionTitle(target.value)}
          sx={wizardTextFieldSx}
        />
        <div className="mt-5">
          <FieldLabel>Question Description</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={editingQuestionDescription}
            onChange={({ target }) => setEditingQuestionDescription(target.value)}
            sx={wizardTextFieldSx}
          />
        </div>
      </LibraryEditDialog>
    </>
  );
};

export default Step1EventDetails;
